import React, { useState } from "react"
import styled from "@emotion/styled"
import * as layouts from "layouts"
import * as api from "../api"

interface SuggestionPanelProps {
	asset: api.AssetExtract
	onApprove: () => Promise<void>
	onReject: () => Promise<void>
	onConfigure: () => void
}

export function SuggestionPanel({ asset, onApprove, onReject, onConfigure }: SuggestionPanelProps): JSX.Element {
	const [loading, setLoading] = useState(false)

	const handleApprove = async (): Promise<void> => {
		setLoading(true)
		try {
			await onApprove()
		} finally {
			setLoading(false)
		}
	}

	const handleReject = async (): Promise<void> => {
		setLoading(true)
		try {
			await onReject()
		} finally {
			setLoading(false)
		}
	}

	if (asset.review_status === "pending") {
		return (
			<Panel $status="pending">
				<Question>Is this asset relevant for your brand?</Question>
				<ButtonGroup>
					<layouts.buttons.primary flex="1" onClick={handleApprove} disabled={loading}>
						{loading ? "..." : "Yes"}
					</layouts.buttons.primary>
					<layouts.buttons.outlinered flex="1" onClick={handleReject} disabled={loading}>
						{loading ? "..." : "No"}
					</layouts.buttons.outlinered>
				</ButtonGroup>
			</Panel>
		)
	}

	if (asset.review_status === "approved") {
		return (
			<Panel $status="approved">
				{asset.sent_to_settings ? (
					<Status>✓ Sent to Settings</Status>
				) : asset.configured_as ? (
					<>
						<Status>✓ Approved - Configured as {asset.configured_as}</Status>
						<layouts.buttons.outline onClick={onConfigure} fontSize="12px" padding="6px 12px">
							Edit Configuration
						</layouts.buttons.outline>
					</>
				) : (
					<>
						<Status>✓ Approved - Awaiting Configuration</Status>
						<layouts.buttons.primary onClick={onConfigure} fontSize="12px" padding="6px 12px">
							Configure Now
						</layouts.buttons.primary>
					</>
				)}
			</Panel>
		)
	}

	if (asset.review_status === "rejected") {
		return (
			<Panel $status="rejected">
				<Status>✗ Rejected</Status>
			</Panel>
		)
	}

	return <></>
}

const Panel = styled.div<{ $status?: string }>`
	padding: 15px;
	background: ${(props) =>
		props.$status === "approved" ? "#e8f4f8" : props.$status === "rejected" ? "#fdeef0" : "#f5f5f5"};
	border-top: 1px solid #e0e0e0;
`

const Question = styled.div`
	font-size: 13px;
	font-weight: 600;
	color: #616161;
	margin-bottom: 10px;
`

const ButtonGroup = styled.div`
	display: flex;
	gap: 10px;
`

const Status = styled.div`
	font-size: 13px;
	font-weight: 600;
	color: #424242;
	margin-bottom: 8px;
`
